import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Download, ShieldAlert, Loader2, Copy, Check, Clock, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Toxic Link Disavow panel — lets user set a toxicity threshold and download
 * a Google-ready `disavow.txt` file. Shows a live preview of the file.
 */
export function DisavowPanel({ projectId }) {
    const [threshold, setThreshold] = useState(60);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const load = async (t) => {
        setLoading(true);
        try {
            const r = await client.get(`/projects/${projectId}/disavow/preview`, { params: { threshold: t } });
            setPreview(r.data);
        } catch (e) {
            toast.error("Preview failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (projectId) { load(threshold); loadHistory(); } /* eslint-disable-next-line */ }, [projectId]);

    const rerun = () => load(threshold);

    const loadHistory = async () => {
        if (!projectId) return;
        setHistoryLoading(true);
        try {
            const r = await client.get(`/projects/${projectId}/disavow/history`);
            setHistory(r.data.exports || []);
        } catch { /* ignore */ }
        finally { setHistoryLoading(false); }
    };

    const download = async () => {
        try {
            const res = await client.get(`/projects/${projectId}/disavow.txt`, {
                params: { threshold },
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/plain" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = preview?.filename || "disavow.txt";
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`Downloaded — ${preview?.count || 0} domains`);
            loadHistory();
        } catch (e) {
            toast.error("Download failed");
        }
    };

    const redownload = async (exp) => {
        try {
            const res = await client.get(`/disavow/history/${exp.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/plain" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = exp.filename || "disavow.txt";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Historical file downloaded");
        } catch {
            toast.error("Download failed");
        }
    };

    const removeExport = async (exp) => {
        if (!confirm(`Delete the export from ${new Date(exp.created_at).toLocaleString()}?`)) return;
        try {
            await client.delete(`/disavow/history/${exp.id}`);
            setHistory((h) => h.filter((x) => x.id !== exp.id));
            toast.success("Deleted from history");
        } catch {
            toast.error("Delete failed");
        }
    };

    const copyToClipboard = async () => {
        if (!preview?.file_preview) return;
        try {
            await navigator.clipboard.writeText(preview.file_preview);
            setCopied(true);
            toast.success("Disavow file copied");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div className="border border-destructive/40 bg-destructive/5 p-6" data-testid="disavow-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline text-destructive flex items-center gap-2">
                        <ShieldAlert className="w-3 h-3" /> DISAVOW EXPORT
                    </div>
                    <div className="font-display text-xl mt-1">One click. Google-ready.</div>
                    <p className="text-sm text-muted-foreground mt-1 max-w-lg">
                        Draft a <code className="text-xs bg-background border border-border px-1.5 py-0.5">disavow.txt</code> file
                        from your flagged toxic referring domains. Upload it to Search Console → Disavow Tool.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Domains</div>
                        <div className="font-mono text-3xl text-destructive" data-testid="disavow-count">{preview?.count ?? "—"}</div>
                    </div>
                    <button
                        data-testid="disavow-download-btn"
                        onClick={download}
                        disabled={!preview || preview.count === 0}
                        className="bg-destructive text-destructive-foreground px-4 py-3 text-sm font-medium hover:bg-destructive/90 flex items-center gap-2 disabled:opacity-50"
                    >
                        <Download className="w-4 h-4" /> Download disavow.txt
                    </button>
                </div>
            </div>

            {/* Threshold slider */}
            <div className="mt-6 flex items-center gap-4 flex-wrap">
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Toxicity threshold ≥</label>
                <input
                    data-testid="disavow-threshold"
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                    onMouseUp={rerun}
                    onTouchEnd={rerun}
                    className="flex-1 max-w-md accent-destructive"
                />
                <div className="font-mono text-lg text-destructive w-12 text-right">{threshold}</div>
                <div className="text-xs text-muted-foreground">
                    (recommended: 60 for cautious, 40 for aggressive)
                </div>
            </div>

            {/* Domain list */}
            {loading ? (
                <div className="mt-6 flex items-center justify-center py-8 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Rebuilding preview…
                </div>
            ) : preview && preview.count === 0 ? (
                <div className="mt-6 border border-border p-4 text-sm text-muted-foreground text-center" data-testid="disavow-empty">
                    Nothing to disavow at threshold {threshold}. Clean profile — or try lowering the threshold.
                </div>
            ) : preview ? (
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                        <div className="overline mb-2">Flagged domains ({preview.count})</div>
                        <div className="border border-border max-h-64 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/40 sticky top-0">
                                    <tr>
                                        <th className="text-left p-2">Domain</th>
                                        <th className="text-right p-2">Tox</th>
                                        <th className="text-right p-2">DR</th>
                                        <th className="text-right p-2">Links</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.domains.map((d) => (
                                        <tr key={d.domain} className="border-t border-border/50">
                                            <td className="p-2 font-mono">{d.domain}</td>
                                            <td className="p-2 text-right font-mono text-destructive">{d.toxic_score}</td>
                                            <td className="p-2 text-right font-mono text-muted-foreground">{d.dr}</td>
                                            <td className="p-2 text-right font-mono text-muted-foreground">{d.backlinks}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="overline">File preview</div>
                            <button
                                data-testid="disavow-copy-btn"
                                onClick={copyToClipboard}
                                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                            >
                                {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        </div>
                        <pre
                            data-testid="disavow-preview"
                            className="text-[11px] font-mono bg-background border border-border p-3 leading-relaxed max-h-64 overflow-auto whitespace-pre"
                        >
{preview.file_preview}
                        </pre>
                    </div>
                </div>
            ) : null}

            {/* History timeline */}
            <div className="mt-6 border-t border-border/60 pt-5" data-testid="disavow-history">
                <div className="flex items-center justify-between mb-3">
                    <div className="overline flex items-center gap-2">
                        <Clock className="w-3 h-3" /> Export history
                        {history.length > 0 && <span className="font-mono text-muted-foreground">({history.length})</span>}
                    </div>
                    <button
                        onClick={loadHistory}
                        title="Refresh"
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    >
                        <RefreshCw className={`w-3 h-3 ${historyLoading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
                {historyLoading && history.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Loading…</div>
                ) : history.length === 0 ? (
                    <div className="text-xs text-muted-foreground border border-dashed border-border p-4 text-center">
                        No exports yet. Downloads are automatically logged here for client audit trails.
                    </div>
                ) : (
                    <div className="border border-border max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/40 sticky top-0">
                                <tr>
                                    <th className="text-left p-2">When</th>
                                    <th className="text-right p-2">Threshold</th>
                                    <th className="text-right p-2">Domains</th>
                                    <th className="text-left p-2">File</th>
                                    <th className="p-2 w-16"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((exp) => (
                                    <tr key={exp.id} className="border-t border-border/50" data-testid={`history-row-${exp.id}`}>
                                        <td className="p-2 font-mono text-muted-foreground">
                                            {new Date(exp.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                        </td>
                                        <td className="p-2 text-right font-mono">≥ {exp.threshold}</td>
                                        <td className="p-2 text-right font-mono text-destructive">{exp.count}</td>
                                        <td className="p-2 font-mono text-muted-foreground truncate max-w-[280px]">{exp.filename}</td>
                                        <td className="p-2">
                                            <div className="flex items-center gap-2 justify-end">
                                                <button
                                                    onClick={() => redownload(exp)}
                                                    title="Re-download"
                                                    className="text-muted-foreground hover:text-primary p-1"
                                                    data-testid={`history-redownload-${exp.id}`}
                                                >
                                                    <Download className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => removeExport(exp)}
                                                    title="Delete"
                                                    className="text-muted-foreground hover:text-destructive p-1"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
